import React, { PropsWithChildren } from "react";

import { useWorkOSAuth } from "@worktango/d2c-service/workOSReact";
import { useBooleanFlagValue } from "@worktango/feature-flags-react";

import { Button, Flex, Spacer, ThemeToggle } from "@kazoohr/confetti";
import { coalesce, coalesceFirstTruthy, doNothing } from "@kazoohr/helpers";
import { useTranslation } from "@kazoohr/internationalization";
import { Link, useLocation } from "@kazoohr/router";

import {
  GetCurrentUserQuery,
  useGetCurrentUserQuery,
} from "../../../generated/apiHooks";
import { isActiveSessionPage } from "../../helpers/isActiveSessionPage";
import { ConstellationLogo } from "../../static/ConstellationLogo";
import { PoweredBy } from "../../static/PoweredBy";
import { DevAuthTogglePlatformAdmin } from "../devAuth/DevAuthTogglePlatformAdmin";
import { ViewingAsBar } from "../ViewingAsBar";

import { useDarkModeEnabled } from "./ConstellationThemeProvider";
import styles from "./constellationWrapper.scss";
import { MaintenanceModeBanner } from "./MaintenanceModeBanner";

export function ConstellationWrapper({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const hideHeader = isActiveSessionPage(pathname);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <MaintenanceModeBanner />
      <ViewingAsBar />
      <ConstellationHeader />
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        style={{
          width: "100%",
          padding: hideHeader ? "40px 40px 40px" : "0px 40px 40px",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <div className={styles["constellation-wrapper"]}>{children}</div>
        <Flex
          direction="column"
          alignItems="center"
          gap="16px"
          style={{ marginTop: "40px" }}
        >
          <PoweredBy />
          <Flex direction="row" gap="16px" alignItems="center">
            <Button
              to="/privacy"
              linkElement={Link}
              variant="link"
              size="small"
            >
              {t("content:navigation.privacyPolicy")}
            </Button>
            <Button
              to="/terms-of-use"
              linkElement={Link}
              variant="link"
              size="small"
            >
              {t("content:navigation.termsOfService")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
}

function getName(
  user?: null | {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  }
) {
  const name = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return coalesceFirstTruthy(name, user?.email);
}

function ConstellationHeader() {
  const { state, user } = useWorkOSAuth();
  const { pathname } = useLocation();
  const darkModeEnabled = useDarkModeEnabled();

  if (isActiveSessionPage(pathname)) {
    return null;
  }

  return (
    <div className={styles["constellation-header"]}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className={styles["constellation-header-inner"]}
      >
        <Link style={{ color: "inherit" }} to="/">
          <Flex alignItems="center" gap="16px">
            <ConstellationLogo width={240} />
            <span className="screen-reader-only">Constellation</span>
          </Flex>
        </Link>
        <Flex gap="24px" justifyContent="flex-end" alignItems="center">
          {darkModeEnabled && <ThemeToggle compact />}
          {state !== "loading" && user ? (
            <AuthenticatedHeaderContent />
          ) : (
            <UnauthenticatedHeaderContent />
          )}
        </Flex>
      </Flex>
      <DevAuthTogglePlatformAdmin />
    </div>
  );
}

function isPlatformAdmin(
  currentUser?: GetCurrentUserQuery["currentUser"] | null
) {
  return coalesce(currentUser?.isPlatformAdmin, false);
}

function isOrgAdmin(currentUser?: GetCurrentUserQuery["currentUser"] | null) {
  return coalesce(
    currentUser?.organizationMemberships.some((org) => org.role === "ADMIN"),
    false
  );
}

function canAdministerOrganization(
  currentUser?: GetCurrentUserQuery["currentUser"] | null
) {
  const isAccessingOrganization =
    isPlatformAdmin(currentUser) &&
    coalesce(currentUser?.organizationOverridden, false);
  return isAccessingOrganization || isOrgAdmin(currentUser);
}

function AuthenticatedHeaderContent() {
  const { t } = useTranslation();
  const { user, signOut } = useWorkOSAuth();
  const { data } = useGetCurrentUserQuery({
    onError: doNothing, // Handled elsewhere
  });
  const userName = getName(user);
  const { pathname } = useLocation();
  const isOnOrgAdminPage =
    pathname.includes("/organization") && !pathname.includes("/admin");
  const isOnPlatformAdminPage = pathname.includes("/admin");

  return (
    <>
      <div>{userName}</div>
      <Spacer size="small" orientation="horizontal" />
      {isPlatformAdmin(data?.currentUser) && (
        <Button
          data-testid="platform-admin-button"
          to="/admin"
          linkElement={Link}
          variant={isOnPlatformAdminPage ? "link" : "system"}
          icon="admin"
        >
          {t("content:navigation.platformAdmin")}
        </Button>
      )}
      {canAdministerOrganization(data?.currentUser) && (
        <Button
          data-testid="admin-button"
          to="/organization"
          linkElement={Link}
          variant={isOnOrgAdminPage ? "link" : "system"}
        >
          {t("content:navigation.admin")}
        </Button>
      )}
      <Button onClick={signOut} variant="system">
        {t("content:navigation.signOut")}
      </Button>
    </>
  );
}

function UnauthenticatedHeaderContent() {
  const { t } = useTranslation();
  const { signIn, signUp } = useWorkOSAuth();
  const isSignUpAllowed = useBooleanFlagValue("allow-signup", false);

  return (
    <>
      <Button onClick={signIn} variant="system">
        {t("content:navigation.signIn")}
      </Button>
      {isSignUpAllowed && (
        <Button onClick={signUp} variant="primary">
          {t("content:navigation.signUp")}
        </Button>
      )}
    </>
  );
}
